import { useState, type CSSProperties } from 'react';
import { PageShell, Section } from '~/components/layout';
import { CtaBanner } from '~/components/sections';
import { ButtonLink } from '~/components/ui';
import { perksHero, partners, perkGroups, type Partner, type PerkGroup } from '~/content/perks';
import { cn } from '~/lib/cn';

type Filter = 'All' | PerkGroup;
const filters: Filter[] = ['All', ...perkGroups];

/**
 * Best-effort initials for the monogram fallback tile. Strips punctuation and
 * uses the first letter of the first one or two words (e.g. "Be Filing" → "BF",
 * "Softileo" → "S").
 */
function monogram(name: string) {
  const words = name
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const initials = words
    .slice(0, 2)
    .map((word) => word[0])
    .join('');
  return (initials || name.slice(0, 1)).toUpperCase();
}

export function PerksPage() {
  const [selected, setSelected] = useState<Filter>('All');
  const visiblePartners =
    selected === 'All' ? partners : partners.filter((partner) => partner.group === selected);

  return (
    <PageShell>
      {/* Hero: centred headline + CTA (standard hero style). */}
      <Section className="pt-16 !pb-0 md:pt-24">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-1 text-center md:gap-6">
          <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance md:text-title-m">
            {perksHero.title}
          </h1>
          <ButtonLink href={perksHero.cta.href} size="md" className="mt-4">
            {perksHero.cta.label}
          </ButtonLink>
        </div>
      </Section>

      {/* Filter sidebar + partner grid. Hero→content spacing matches /compare:
          hero !pb-0 + this section's !pt-[104px]/md:!pt-[120px] (104px mobile,
          120px desktop). Bottom padding of 200px matches the standard gap before
          the CtaBanner band (the FaqAccordion's !pb-[200px] on /compare, /rewards
          etc.), since the grid sits directly above the red CTA here.

          On the 9-col base grid the filters take the left 3 cols and the cards the
          right 6 (rendered as a 2-col card grid). Below lg the filters collapse to
          a wrapping row above the cards, which drop to a single column. */}
      <Section className="!pt-[84px] !pb-[200px] md:!pt-[120px]">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-9 lg:gap-5">
          {/* Left: filter buttons. Sticky on desktop so they stay in view while
              scrolling the partner grid; they wrap rather than stacking full-width. */}
          <div className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start">
            <div className="flex flex-row flex-wrap gap-3">
              {filters.map((filter, i) => {
                const isSelected = filter === selected;
                return (
                  <button
                    key={filter}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSelected(filter)}
                    style={{ '--stagger': `${i * 60}ms` } as CSSProperties}
                    className={cn(
                      // Padding/height copied from the size="md" ButtonLink so the
                      // filter chips match the "Get the app" CTA spacing.
                      'filter-pill-in inline-flex min-w-16 cursor-pointer items-center justify-center rounded-pill border pt-[12px] pr-[14px] pb-[13px] pl-[14px] text-[16px] font-medium tracking-[0.192px] text-ink transition-colors',
                      isSelected
                        ? 'border-transparent bg-grey-50'
                        : 'border-grey-100 bg-white hover:bg-grey-50',
                    )}>
                    <span>{filter}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: partner cards (2-col grid within the right 6 cols). */}
          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {visiblePartners.map((partner) => (
                <PartnerCard key={partner.name} partner={partner} />
              ))}
            </div>
          </div>
        </div>
      </Section>

      <CtaBanner />
    </PageShell>
  );
}

function PartnerCard({ partner }: { partner: Partner }) {
  const isExternal = !partner.href.startsWith('/');
  const className =
    'group flex flex-col gap-7 rounded-none border border-black-6 bg-white p-6 transition-colors duration-200 ease-nav hover:border-transparent hover:bg-grey-50 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink';
  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        {/* Real partner logo (from nsave.com/perks) on a clean white tile; falls
            back to a monogram tile when a logo is unavailable. */}
        {partner.logo ? (
          <img
            src={partner.logo}
            alt={partner.name}
            className="h-auto max-h-10 w-auto max-w-[160px] shrink-0 object-contain"
          />
        ) : (
          <span
            aria-hidden="true"
            className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-grey-50 text-h5 font-semibold text-ink">
            {monogram(partner.name)}
          </span>
        )}
        {partner.offer && (
          <span className="rounded-none bg-red-50 p-1 text-body font-medium text-red-500">
            {partner.offer}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <h3 className="text-h5 font-semibold text-ink">{partner.name}</h3>
        <p className="text-body text-grey-900">{partner.description}</p>
      </div>
    </>
  );

  return (
    <a
      href={partner.href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      className={className}>
      {body}
    </a>
  );
}
